import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  Link,
  Checkbox,
  useToast,
  InputGroup,
  InputRightElement,
  Image,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { loginUser } from '../services/api';
import { useAuth } from '../hooks/useAuth';

// Component per il canvas delle particelle/sinapsi
const ParticleCanvas = () => {
  const canvasRef = useRef(null);
  const mousePosRef = useRef({ x: null, y: null });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * dpr;
    canvas.height = canvas.offsetHeight * dpr;
    ctx.scale(dpr, dpr);

    const particles = Array.from({ length: 50 }).map(() => ({
      x: Math.random() * canvas.offsetWidth,
      y: Math.random() * canvas.offsetHeight,
      vx: (Math.random() - 0.5) * 0.5,
      vy: (Math.random() - 0.5) * 0.5,
    }));

    const connectDistance = 100;
    const attractRadius = 120;
    const attractStrength = 0.003;
    let animationId;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);

      particles.forEach(p => {
        const { x: mx, y: my } = mousePosRef.current;
        if (mx !== null) {
          const dx = mx - p.x;
          const dy = my - p.y;
          const dist = Math.hypot(dx, dy);
          if (dist < attractRadius) {
            p.vx += dx * attractStrength;
            p.vy += dy * attractStrength;
          }
        }

        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0 || p.x > canvas.offsetWidth) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.offsetHeight) p.vy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(67,180,188,0.7)';
        ctx.fill();
      });

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const pa = particles[i];
          const pb = particles[j];
          const dx = pa.x - pb.x;
          const dy = pa.y - pb.y;
          const dist = Math.hypot(dx, dy);
          if (dist < connectDistance) {
            ctx.beginPath();
            ctx.moveTo(pa.x, pa.y);
            ctx.lineTo(pb.x, pb.y);
            ctx.strokeStyle = `rgba(67,180,188,${1 - dist / connectDistance})`;
            ctx.stroke();
          }
        }
      }

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => cancelAnimationFrame(animationId);
  }, []);

  const handleMouseMove = e => {
    const rect = canvasRef.current.getBoundingClientRect();
    mousePosRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const handleMouseLeave = () => {
    mousePosRef.current = { x: null, y: null };
  };

  return (
    <canvas
      ref={canvasRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
      }}
    />
  );
};

const Login = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { setUser, refreshAuth } = useAuth(); // 👈 Aggiungi refreshAuth

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLogin = async () => {
    setIsSubmitting(true);
    try {
      const response = await loginUser(email, password);
      const storedUser = {
        id: response.data.id,
        token: response.data.token,
        roles: response.data.roles,
      };
      localStorage.setItem('token', storedUser.token);
      localStorage.setItem('user', JSON.stringify(storedUser));
      remember
        ? localStorage.setItem('remember', 'true')
        : localStorage.removeItem('remember');

      setUser(storedUser);
      
      // 👇 CARICA SUBITO customer e discount
      await refreshAuth(storedUser.id);
      
      toast({
        title: 'Accesso riuscito!',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      navigate('/create-quote');
    } catch (error) {
      toast({
        title: 'Errore nel login',
        description: error.response?.data?.message || 'Credenziali errate.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') handleLogin();
  };

  return (
    <>
      <Flex
        position="relative"
        height="100vh"
        background="linear-gradient(135deg, rgba(67,180,188,0.1) 0%, rgba(255,255,255,0.1) 50%, rgba(67,180,188,0.05) 100%)"
        overflow="hidden"
      >
        {/* Background canvas con particelle/sinapsi */}
        <ParticleCanvas />

        {/* Contenuto principale */}
        <Box
          position="relative"
          zIndex={10}
          width="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Box textAlign="center" maxW="320px" position="relative">
            <Box
              position="absolute"
              top="-60px"
              left="50%"
              transform="translateX(-50%)"
              border="1px solid teal"
              width="120px"
              height="120px"
              bg="white"
              borderRadius="full"
              boxShadow="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
              zIndex={11}
            >
              <Image
                src="/assets/e-Zen_Logo-Color.png"
                alt="Logo"
                boxSize="80px"
                objectFit="contain"
              />
            </Box>

            <Box
              bg="white"
              pt="80px"
              pb={8}
              px={8}
              borderWidth="2px"
              borderColor="teal.400"
              borderRadius="md"
              boxShadow="lg"
              transition="all 0.2s"
              _hover={{
                boxShadow: 'xl',
                borderColor: 'teal.600',
                transform: 'scale(1.02)',
              }}
            >
              <Text fontSize="2xl" mb={4}>
                Login
              </Text>

              <Input
                placeholder="Email"
                mb={4}
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={handleKeyDown}
              />

              <InputGroup mb={4}>
                <Input
                  placeholder="Password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <InputRightElement width="3rem">
                  <Button h="1.75rem" size="sm" onClick={() => setShowPassword(s => !s)}>
                    {showPassword ? <ViewOffIcon /> : <ViewIcon />}
                  </Button>
                </InputRightElement>
              </InputGroup>

              <Checkbox mb={4} isChecked={remember} onChange={e => setRemember(e.target.checked)}>
                Ricorda credenziali
              </Checkbox>

              <Button colorScheme="teal" w="full" onClick={handleLogin} isLoading={isSubmitting}>
                Login
              </Button>

              <Text mt={4} fontSize="sm">
                Non hai un account?{' '}<Link color="teal.500" onClick={() => navigate('/register')}>
                  Registrati
                </Link>
              </Text>
            </Box>
          </Box>
        </Box>
      </Flex>
    </>
  );
};

export default Login;
